import React, { useState, useEffect, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Select from "react-select";
import { Alert, Dialog, Snackbar } from "@mui/material";
import "../Candidate-Upload/candidateUpload.scss";
import TimeZone from "../../utils/all_timezones.json";
import {
  fetchAvailableSlots,
  getCandidateHiringStage,
  initiateWebVoiceCall,
  scheduledHiringStage,
} from "../../Redux/ActionCreators/jobsAction";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Vapi from "@vapi-ai/web";

const StageIds = {
  "293e9b95-4610-48f2-ae93-c7264cae7d6a": "Assessment",
  "ed658e02-1549-4d9e-a735-1f1c1e385f46": "Video Call",
  "fd40e3d2-38a9-4cef-937a-93ef6a42d045": "Voice Call",
};

export default function ScheduleStage() {
  const [screenLoading, setScreenLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("Personal info");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [successMessageText, setSuccessMessageText] = useState("");
  const [scheduleDate, setScheduleDate] = useState(moment());
  const [timezone, setTimezone] = useState("");
  const [slotPopupOpen, setSlotPopupOpen] = useState(false);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [showPhoneCheckModal, setShowPhoneCheckModal] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [scheduleCompleted, setScheduleCompleted] = useState(false);
  const [maxScheduleDate, setMaxScheduleDate] = useState(moment().add(15, "days").toDate());
  const [webCallStarted, setWebCallStarted] = useState(false);
  const [micDenied, setMicDenied] = useState(false);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [vapiInstance, setVapiInstance] = useState(null);
  const [webCallCompleted, setWebCallCompleted] = useState(false);
  const [webCallInitializing, setWebCallInitializing] = useState(false);

  const { id } = useParams();
  const dispatch = useDispatch();
  const { candidateHiringStage } = useSelector((state) => state.jobs);
  const availableSlots = useSelector((state) => state.jobs.availableSlots);

  useEffect(() => {
    const fetchJob = async () => {
      if (id) {
        setScreenLoading(true);
        try {
          await dispatch(getCandidateHiringStage(id));
          setLinkExpired(false);
        } catch (error) {
          const status = error?.parsedStatus || error?.response?.status;
          console.error("Unexpected error", error);
          if (status === 403 || status === 410) {
            setLinkExpired(true);
          }
        }
        setScreenLoading(false);
      }
    };
    fetchJob();

    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const matched = TimeZone.find((tz) => tz.value === detected);
    if (matched) {
      setTimezone(matched);
    }
  }, []);

  useEffect(() => {
    if (candidateHiringStage?.job_id) {
      const formattedDate = moment(scheduleDate).format("YYYY-MM-DD");
      dispatch(fetchAvailableSlots(candidateHiringStage.job_id, formattedDate));
    }
  }, [candidateHiringStage?.job_id]);

  useEffect(() => {
    if (candidateHiringStage?.expire_at) {
      setMaxScheduleDate(moment(candidateHiringStage.expire_at).toDate());
    }
  }, [candidateHiringStage?.expire_at]);

  const requestMicPermission = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      return true;
    } catch (err) {
      return false;
    }
  };

  const fullName = candidateHiringStage?.candidate?.name || "";
  const [firstName, lastName] = fullName.split(" ");
  const stageName = StageIds[candidateHiringStage?.hiring_stage_id] || "";
  const candidateId = candidateHiringStage?.candidate?.id || "";
  const jobId = candidateHiringStage?.job_id || "";
  const hiringStatus = candidateHiringStage?.status?.name?.toLowerCase();
  const isOngoingOrCompleted = hiringStatus === "ongoing" || hiringStatus === "completed";
  const isScheduled = candidateHiringStage?.status?.name?.toLowerCase() === "scheduled";
  const webCallButtonText = isScheduled ? "Cancel Scheduled Call & Start Web Call" : "Start a web voice call now";

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleSlotSelection = (id) => {
    setSelectedSlotId(id);
    setSlotPopupOpen(false);
  };

  const handleDateChange = (date) => {
    setScheduleDate(date);
    const formattedDate = moment(date).format("YYYY-MM-DD");
    dispatch(fetchAvailableSlots(candidateHiringStage?.job_id, formattedDate));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await setLoading(true);

    let obj = {
      id: id,
      schedule_date: moment(scheduleDate).format("YYYY-MM-DD"),
      schedule_timezone: timezone ? timezone.value : "",
      slot_id: selectedSlotId,
    };
    const res = await dispatch(
      scheduledHiringStage(obj, candidateHiringStage?.job_id)
    );
    if (res === 200) {
      setSnackbarSeverity("success");
      setSuccessMessage(true);
      setSuccessMessageText("Stage scheduled and email sent successfully");
      setScheduleDate("");
      setTimezone(null);
      setScheduleCompleted(true);
    } else if (res?.success === false) {
      const message = res.message || "Something went wrong during scheduling please contact administrator";
      if (message.toLowerCase().includes("country code")) {
        setShowPhoneCheckModal(true);
        setSuccessMessageText(message);
      } else {
        setSnackbarSeverity("error");
        setSuccessMessage(true);
        setSuccessMessageText(message);
      }
    } else {
      setSnackbarSeverity("error");
      setSuccessMessage(true);
      setSuccessMessageText("Something went wrong during scheduling please contact administrator");
    }
    await setLoading(false);
  };

  const snackClosed = () => {
    setSuccessMessage(false);
    setSuccessMessageText("");
  };

  const isDisabled = scheduleDate && timezone && selectedSlotId;

  const handleInitiateWebCall = async () => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    setMicDenied(false);

    const micAllowed = await requestMicPermission();
    
    if (!micAllowed) {
      setMicDenied(true);
      setSnackbarSeverity("warning");
      setSuccessMessage(true);
      setSuccessMessageText("You have declined the microphone access");
      return;
    }
    
    try {
      setWebCallInitializing(true);

      const res = await dispatch(initiateWebVoiceCall(jobId, candidateId, id));

      if (!res?.data?.payload) {
        throw new Error("No payload received");
      }

      startVapiCall(res.data.payload);
    } catch (err) {
      console.error("Web call init failed", err);

      setSnackbarSeverity("error");
      setSuccessMessage(true);
      setSuccessMessageText(
        "Failed to connect web call. Try again or schedule the voice call."
      );

      setWebCallInitializing(false);
    }
  }
  

  const startVapiCall = (payload) => {
    const vapi = new Vapi(process.env.REACT_APP_VAPI_PUBLIC_KEY);
    setVapiInstance(vapi);

    vapi.start(payload.assistant, payload.assistantOverrides);

    vapi.on("speech-start", () => {
      setWebCallInitializing(false);
      setIsUserSpeaking(true);
      setWebCallStarted(true);
    });

    vapi.on("speech-end", () => {
      setIsUserSpeaking(false);
    });

    vapi.on("call-end", () => {
      cleanupCall(true);
    });

    vapi.on("error", () => {
      setWebCallInitializing(false);
      setSnackbarSeverity("error");
      setSuccessMessage(true);
      setSuccessMessageText(
        "Failed to connect web call. Try again or schedule the voice call."
      );
    });
  };

  const handleEndVoiceCall = () => {
    if (vapiInstance) {
      vapiInstance.stop();
    }
    cleanupCall(true);
  };

  const cleanupCall = (completed = false) => {
    setWebCallStarted(false);
    setIsUserSpeaking(false);
    setVapiInstance(null);

    if (completed) {
      setWebCallCompleted(true);
    }
  };


  if (screenLoading) {
    return (
      <div className="job-summary">
        <div className="loading-overlay">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading job summary...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Fragment>
      <Dialog
        className="schedule-loader"
        open={loading}
        onClose={() => {}}
        PaperProps={{
          sx: {
            boxShadow: "none",
          },
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={successMessage}
        autoHideDuration={3000}
        onClose={snackClosed}
      >
        <Alert onClose={snackClosed} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {successMessageText}
        </Alert>
      </Snackbar>
      {webCallInitializing && (
        <div className="loading-overlay" style={{ zIndex: 9999 }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">
              Connecting to web voice call...
            </span>
          </div>
        </div>
      )}
      {linkExpired ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "24px",
            color: "#ff0000",
          }}
        >
          Link has been expired
        </div>
      ) : (
        <div className="candidate-upload">
          {!webCallInitializing && !webCallStarted && (
            <>
              <div className="candidate-upload-header">
                <div className="candidate-upload-title">
                  <div className="company-logo">
                    <img src="/images/gappeoai-sm-logo.svg" alt="" />
                  </div>
                  <div className="position-details">
                    <h3>{candidateHiringStage?.job?.title}</h3>
                    <div className="requirements">
                      {candidateHiringStage?.job?.skills?.slice(0, 2).map((skills, index) => (
                        <p className="candidate-skills" key={index}>
                          {skills}
                        </p>
                        ))}
                        {candidateHiringStage?.job?.skills?.length > 2 && (
                          <p className="candidate-skills more-skills">
                            +{candidateHiringStage?.job?.skills.length - 2} more
                            <div className="skills-popup">
                              {candidateHiringStage?.job?.skills.slice(2).map((s, idx, arr) => (
                                <span key={idx}>
                                  {s}
                                  {idx < arr.length - 1 && ", "}
                                </span>
                              ))}
                            </div>
                          </p>
                        )}
                        {candidateHiringStage?.job?.salary && 
                          <p style={{ background: "#DEF7EC", color: "#03543F" }}>
                            {candidateHiringStage?.job?.salary}
                          </p>
                        }
                    </div>
                  </div>
                </div>
                <div className="candidate-job-description">
                  <div className="job-description">
                    <h4>Job details</h4>
                    <div className="row">
                      <div className="detail-item d-flex col-md-6 mb-2">
                        <h6>Job Title :- </h6>&nbsp;
                        <p>{candidateHiringStage?.job?.title}</p>
                      </div>
                      <div className="detail-item d-flex col-md-6 mb-2">
                        <h6>Company name :- </h6>&nbsp;
                        <p>{candidateHiringStage?.job?.company_name}</p>
                      </div>
                      <div className="detail-item d-flex col-md-6 mb-2">
                        <h6>Experience :- </h6>&nbsp;
                        <p>{candidateHiringStage?.job?.experience_required}</p>
                      </div>
                      <div className="detail-item d-flex col-md-6 mb-2">
                        <h6>Job qualifications :- </h6>&nbsp;
                        <p>{candidateHiringStage?.job?.qualifications}</p>
                      </div>
                      <div className="detail-item col-md-12 mb-2">
                        <h6>Job description :- </h6>
                        <p>
                          {candidateHiringStage?.job?.description
                            ?.split("\n")
                            .map((line, index) =>
                              line.trim() !== "" ? <p key={index}>{line}</p> : null
                            )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="candidate-upload-body">
                {webCallCompleted ? (
                  <div
                    className="thank-you-screen"
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      background: "#f0fff4",
                      borderRadius: "12px",
                    }}
                  >
                    <h2 style={{ color: "#03543F" }}>
                      Thank you!
                    </h2>
                    <p style={{ fontSize: "18px", color: "#065F46" }}>
                      You have completed your voice interview successfully.
                      <br />
                      You can close the window now.
                    </p>
                  </div>
                  ) : 
                  (
                    <>
                    <div className="tab-navigation">
                      {["Personal info"].map((tab) => (
                        <button
                          key={tab}
                          className={`tab-item ${activeTab === tab ? "active" : ""}`}
                          onClick={() => handleTabChange(tab)}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>
                    <div className="job-details-body">
                      {scheduleCompleted ? (
                        <div
                          className="thank-you-screen"
                          style={{
                            padding: "40px",
                            textAlign: "center",
                            background: "#f0fff4",
                            borderRadius: "12px",
                          }}
                        >
                          <h2 style={{ color: "#03543F" }}>Thank you for your time!</h2>
                          <p style={{ fontSize: "18px", color: "#065F46" }}>
                            Your voice call has been successfully scheduled.
                          </p>
                        </div>
                      ) : activeTab === "Personal info" ? (
                        <>
                            <form readonly>
                              <div className="row">
                                <div className="col-md-6">
                                  <div className="form-section">
                                    <label>First Name *</label>
                                    <input
                                      type="text"
                                      name="first_name"
                                      placeholder="Input text"
                                      value={firstName}
                                      readonly
                                    />
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="form-section">
                                    <label>Last Name *</label>
                                    <input
                                      type="text"
                                      name="last_name"
                                      placeholder="Input text"
                                      value={lastName}
                                      readonly
                                    />
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="form-section">
                                    <label>Email ID</label>
                                    <input
                                      type="email"
                                      name="email"
                                      placeholder="Input text"
                                      value={candidateHiringStage?.candidate?.email}
                                      readonly
                                    />
                                  </div>
                                </div>
                                <div className="col-md-6">
                                  <div className="form-section">
                                    <label>Phone number</label>
                                    <input
                                      type="text"
                                      name="phone_number"
                                      placeholder="Input text"
                                      value={
                                        candidateHiringStage?.candidate?.phone_number
                                      }
                                      readonly
                                    />
                                  </div>
                                </div>
                              </div>
                            </form>
                            {/* <button className="btn btn-primary mb-3 mt-3" onClick={handleInitiateWebCall} disabled={ webCallInitializing || webCallStarted || isOngoingOrCompleted}>{webCallButtonText}</button> */}
                            {micDenied && (
                              <p style={{ color: "red", marginTop: "8px" }}>
                                You can take the voice interview only if you allow microphone access.
                                Please enable the microphone by clicking the settings icon near the
                                browser address bar.
                              </p>
                            )}
                            {isOngoingOrCompleted && (
                              <p style={{ color: "#B91C1C", marginTop: "12px", fontWeight: 500 }}>
                                Your voice call has been {hiringStatus}. You cannot take it again.
                              </p>
                            )}
                            <div className="row">
                              <form
                                onSubmit={handleSubmit}
                                className="col-md-6"
                                style={{ padding: "20px" }}
                              >
                                <h3 style={{ color: "#111928", marginBottom: "30px" }}>
                                  {stageName && `Scheduling for ${stageName}`}
                                </h3>
                                <div className="form-section">
                                  <label>Select Date:</label>
                                  <DatePicker
                                    selected={scheduleDate?.toDate?.() || scheduleDate}
                                    onChange={handleDateChange}
                                    dateFormat="dd-MM-yyyy"
                                    minDate={new Date()}
                                    maxDate={maxScheduleDate}
                                    required
                                    className="form-control"
                                  />
                                </div>
                                <div className="mb-2">
                                  <label className="form-label">Timezone:</label>
                                  <Select
                                    options={TimeZone}
                                    value={timezone}
                                    onChange={setTimezone}
                                    placeholder="Select Timezone"
                                    isClearable
                                  />
                                </div>
                                <div className="form-section">
                                  <label>Select Time slot:</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    readOnly
                                    value={
                                      selectedSlotId
                                        ? availableSlots.find(
                                            (s) => s.id === selectedSlotId
                                          )?.display || "Selected"
                                        : ""
                                    }
                                    placeholder="Click to select a time slot"
                                    onClick={() => setSlotPopupOpen(true)}
                                    style={{ cursor: "pointer" }}
                                  />
                                </div>
                                <button
                                  type="submit"
                                  disabled={!isDisabled || isOngoingOrCompleted}
                                  className="btn btn-success"
                                >
                                  Schedule
                                </button>
                                {isOngoingOrCompleted && (
                                  <p style={{ color: "#B91C1C", marginTop: "12px", fontWeight: 500 }}>
                                    Your voice call has been {hiringStatus}. You cannot take it again.
                                  </p>
                                )}
                              </form>
                            </div>
                        </>
                      ) :  null}
                    </div>
                    </>
                  )}
              </div>
            </>
          )}
          {webCallStarted && (
            <div className="d-flex justify-content-center" style={{width:'100%', height:'auto'}}>
              <div className="web-call-container">
                <div className="call-center">
                  <h4>Gappeo is one of the finest AI Assessments product</h4>
                  <div className="call-div">
                    {isUserSpeaking ? (
                      <img src="/images/reddot-gif-img.svg" alt="Speaking" />
                    ) : (
                      <img src="/images/reddot-img.svg" alt="Listening" />
                    )}
                  </div>
                  <button className="btn btn-danger mt-4" onClick={handleEndVoiceCall}>
                    End Voice Call
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {showPhoneCheckModal && (
        <div className="upload-modal">
          <div
            className="modal-content"
            style={{ width: "500px", height: "auto" }}
          >
            <div className="modal-head">
              <h3>Phone Number Check</h3>
              <img
                src="/images/close-icon.svg"
                alt="Close"
                style={{ cursor: "pointer" }}
                onClick={() => setShowPhoneCheckModal(false)}
              />
            </div>
            <div className="modal-input" style={{ padding: "20px" }}>
              <p>{successMessageText}</p>
            </div>
            <div className="d-flex justify-content-center">
              <button
                className="upload-btn"
                onClick={() => setShowPhoneCheckModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {slotPopupOpen && (
        <div className="slot-popup-overlay">
          <div className="slot-popup-content">
            <div className="d-flex justify-content-evenly">
              <div className="d-flex flex-column align-items-center">
                <h4>Select a Time</h4>
                <p>Duration: 15 min</p>
              </div>
              <img
                className="cursor-pointer"
                src="/images/close.svg"
                alt=""
                onClick={() => setSlotPopupOpen(false)}
              />
            </div>
            <div className="slot-list">
              {availableSlots.map((slot) => (
                <button
                  key={slot.id}
                  className="slot-btn"
                  onClick={() => handleSlotSelection(slot.id)}
                >
                  {slot.display || moment(slot.time_of_day).format("h:mma")}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
}
